import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import type { InteractionMode } from '../../hooks/useWorkspace';

interface ModeSelectorProps {
  interactionMode: InteractionMode;
  resolvedMode: 'ask' | 'explore' | 'build' | null;
  onChange: (mode: InteractionMode) => void;
}

const MODES: { key: 'ask' | 'explore' | 'build'; label: string; icon: string }[] = [
  { key: 'ask', label: 'Ask', icon: '💬' },
  { key: 'explore', label: 'Explore', icon: '🧭' },
  { key: 'build', label: 'Build', icon: '🔨' },
];

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to   { opacity: 1; transform: scale(1); }
`;

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0;
  background: var(--bg-elevated, #f5f5f5);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 999px;
  padding: 2px;
`;

const AutoLabel = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--text-muted, #999);
  font-family: inherit;
  white-space: nowrap;
  user-select: none;
`;

const ModeBtn = styled.button<{
  $active: boolean;
  $autoResolved: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.625rem;
  border: none;
  border-radius: 999px;
  font-family: inherit;
  font-size: 0.6875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  white-space: nowrap;
  background: none;
  color: var(--text-muted, #999);

  ${({ $active, $autoResolved }) =>
    $active &&
    !$autoResolved &&
    css`
      background: var(--accent-subtle, rgba(99, 102, 241, 0.08));
      color: var(--accent, #6366f1);
      font-weight: 600;
      box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.08));
    `}

  ${({ $autoResolved }) =>
    $autoResolved &&
    css`
      background: var(--accent-subtle, rgba(99, 102, 241, 0.08));
      color: var(--accent, #6366f1);
      opacity: 0.7;
    `}

  &:hover {
    color: var(--text-primary, #333);
    background: var(--bg-surface, #fff);
  }
`;

const IconSpan = styled.span`
  font-size: 0.75rem;
  line-height: 1;
  flex-shrink: 0;
`;

const AutoBadge = styled.span`
  font-size: 0.5rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: var(--bg-elevated, #f5f5f5);
  color: var(--text-muted, #999);
  border-radius: 999px;
  padding: 0.0625rem 0.25rem;
  position: absolute;
  top: -4px;
  right: -2px;
  animation: ${fadeIn} 0.25s ease-out;
`;

const ModeSelector: React.FC<ModeSelectorProps> = ({
  interactionMode,
  resolvedMode,
  onChange,
}) => {
  const isAuto = interactionMode === 'auto';
  const noneExplicitlySelected = isAuto;

  const handleClick = (key: 'ask' | 'explore' | 'build') => {
    if (!isAuto && interactionMode === key) {
      onChange('auto');
    } else {
      onChange(key);
    }
  };

  return (
    <Container>
      {noneExplicitlySelected && !resolvedMode && <AutoLabel>Auto</AutoLabel>}
      {MODES.map((m) => {
        const isExplicitActive = !isAuto && interactionMode === m.key;
        const isAutoResolved = isAuto && resolvedMode === m.key;
        const isActive = isExplicitActive || isAutoResolved;

        return (
          <ModeBtn
            key={m.key}
            $active={isActive}
            $autoResolved={isAutoResolved}
            onClick={() => handleClick(m.key)}
            title={m.label}
            type="button"
          >
            <IconSpan>{m.icon}</IconSpan>
            {m.label}
            {isAutoResolved && <AutoBadge>auto</AutoBadge>}
          </ModeBtn>
        );
      })}
    </Container>
  );
};

export default ModeSelector;
